import { Link } from 'react-router'
import type { NodeInfo, NodeRef } from '../../api'
import { routePath, type NoPathStream, type RelayRoute } from './api'
import { clock, ms } from './format'
import { NO_PATH_TEXT, ROUTE_SPEC, reasonText } from './states'
import { EpochTag, StateBadge } from './ui'
import { NodeName, PathLine, roleAt } from './path'
import { useCenterNow } from './clock'

/*
 * Streams → the "Route" block of a stream (design #r0 (a)): the relay path
 * ingress → hub, the route and hop states, the epoch, the hop latency and a
 * link to the route card. A stream without relay has no block; a stream the
 * center could not route says so in words (no_path).
 */

export interface RouteBlockProps {
  stream: string
  route?: RelayRoute
  /** the stream is on a via_center node without a route (NodeRelay.no_path) */
  noPath?: NoPathStream
  nodes: ReadonlyMap<string, NodeInfo>
}

export function RouteBlock({ stream, route, noPath, nodes }: RouteBlockProps) {
  const now = useCenterNow(1000)
  if (!route && !noPath) return null
  const rolesOf = (id: string) => nodes.get(id)?.roles
  const offline = (id: string) => {
    const n = nodes.get(id)
    return !!n && n.status !== 'online'
  }

  if (!route || route.state === 'no_path') {
    const ingressId = route?.path[0]?.id ?? noPath!.ingress_node_id
    const ing = nodes.get(ingressId)
    const ingress: NodeRef = route?.path[0] ?? (ing ? { id: ing.id, name: ing.name, region: ing.region } : { id: ingressId, name: ingressId, region: '' })
    const why = route ? reasonText(route.state_reason) : NO_PATH_TEXT[noPath!.reason] ?? noPath!.reason
    const since = route?.state_since ?? noPath!.since
    const grace = route?.source_grace_until ? Math.ceil((Date.parse(route.source_grace_until) - now) / 1000) : undefined
    return (
      <div data-testid={`route-block-${stream}`} data-state="no_path" className="mt-2 flex flex-col gap-1 rounded-md border border-fail/40 bg-fail/[.04] px-2.5 py-2">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
          <span className="mono-label">ROUTE</span>
          <StateBadge spec={ROUTE_SPEC.no_path} />
          {route && route.path.length > 1 ? (
            <PathLine path={route.path} hops={route.hops} rolesOf={rolesOf} offline={offline} />
          ) : (
            <>
              <NodeName node={ingress} role={roleAt(0, 2, rolesOf(ingress.id))} offline={offline(ingress.id)} />
              <span className="text-faint" aria-hidden>
                →
              </span>
              <span className="font-mono text-xs text-fail">no hub</span>
            </>
          )}
          {route && (
            <Link to={routePath(route.route_id)} className="ml-auto text-xs text-accent underline underline-offset-2" data-testid={`route-link-${stream}`}>
              Route card {route.route_id}
            </Link>
          )}
        </div>
        <p className="text-xs text-fail" data-testid={`route-reason-${stream}`}>
          This stream does not reach the platforms through the relay: {why}. Since <span className="tabular">{clock(since)}</span>.
          {grace !== undefined && grace > 0 && (
            <span className="text-text-soft">
              {' '}
              Outputs hold the platform sessions for <span className="tabular">{grace} s</span> more.
            </span>
          )}
          {grace !== undefined && grace <= 0 && <span className="text-text-soft"> The outputs' hold ended.</span>}
        </p>
      </div>
    )
  }

  const r = route
  const spec = ROUTE_SPEC[r.state]
  const hop = r.hops[0]
  const why = reasonText(r.state_reason)
  const settled = r.state === 'active' && (!hop || hop.state === 'up')
  return (
    <div
      data-testid={`route-block-${stream}`}
      data-state={r.state}
      className={`mt-2 flex flex-col gap-1 rounded-md border px-2.5 py-2 ${settled ? 'border-line-2' : spec.color === 'fail' ? 'border-fail/40 bg-fail/[.04]' : 'border-dashed border-warn/50 bg-warn/[.04]'}`}
    >
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
        <span className="mono-label">ROUTE</span>
        <StateBadge spec={spec} testId={`route-state-${stream}`} />
        <PathLine path={r.path} hops={r.hops} rolesOf={rolesOf} offline={offline} />
        <EpochTag e={r.epoch} />
        <Link to={routePath(r.route_id)} className="text-xs text-accent underline underline-offset-2 sm:ml-auto" data-testid={`route-link-${stream}`}>
          Route card <span className="font-mono">{r.route_id}</span>
        </Link>
      </div>
      <p className="num text-xs text-muted">
        {hop && hop.state === 'up' ? (
          <>
            Hop latency p50 <span className="text-text-soft">{ms(hop.lat_p50_ms)}</span> / p99 <span className="text-text-soft">{ms(hop.lat_p99_ms)}</span>
          </>
        ) : hop ? (
          <span className="text-warn">
            Hop {hop.from.name} → {hop.to.name}: {hop.state === 'resuming' ? 'reconnecting, catching up from the ingress ring' : hop.state}
          </span>
        ) : null}
        {r.e2e_p50_ms != null && <> · end-to-end {ms(r.e2e_p50_ms)}</>}
        {' · '}
        {r.last_resume?.at ? (
          <>
            last resume {clock(r.last_resume.at)} ({r.last_resume.result ?? '—'}, {r.last_resume.gap_frames ?? 0} frames lost)
          </>
        ) : (
          'no resumes'
        )}
        {why && !(r.state_reason === 'resuming' && hop?.state === 'resuming') && <span className={spec.color === 'fail' ? 'text-fail' : 'text-warn'}> · {why}</span>}
      </p>
    </div>
  )
}
